import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AuthService } from '../auth';
import { timezoneOffset } from '../date-time';
import { SupportedTags } from '../platform';
import { Platform } from '../platform/Platform';
import { Tags } from '../tags';
import { cleanEndSlash, cleanSlashes } from '../utils/strings/cleanSlash';
import { ensureStartSlash } from '../utils/strings/ensureSlash';
import { DEFAULT_API_OPTIONS, IAPIOptions } from './APIOptions';
import { REJECTION_REASONS } from './constants';
import { ENDPOINTS } from './Endpoints';
import { RequestInterceptor } from './RequestInterceptor';
import { ReturnOnly } from './ReturnOnly';
import { cleanDataResponse } from './utils';

/**
 * Singleton API service that contain helpers to create axios instances,
 * endpoints, sending requests, etc. By adding default helpers like, handling
 * resource fields (return_only), adding the authorization when auth it's present,
 * normalize response data, handle auth errors executing logout when auth errors are thrown.
 */
export class APIService {
    static API_GATEWAY = 'wotr.co';
    static API_GATEWAY_WEBSOCKET = 'de2czzp1w3.execute-api.us-west-2.amazonaws.com';

    private static __SELF__: APIService;
    private _configured = false;
    private _http!: AxiosInstance | null;
    private _api!: AxiosInstance | null;
    private _internal!: AxiosInstance | null;

    /**
     * API service options
     */
    public config: Partial<IAPIOptions> = DEFAULT_API_OPTIONS;

    /**
     * Default http endpoint created automatically by the service, pointing to
     * `${config.baseUrl}`
     */
    static get http(): AxiosInstance {
        return APIService.getInstance().http;
    }

    /**
     * Default api endpoint created automatically by the service, pointing to
     * `${config.baseUrl/api}`
     */
    static get api(): AxiosInstance {
        return APIService.getInstance().api;
    }

    /**
     * Default api endpoint created automatically by the service, pointing to
     * `${config.baseUrl/api/v2}`
     */
    static get internal(): AxiosInstance {
        return APIService.getInstance().internal;
    }

    /**
     * Get default service instance
     */
    public static getInstance(): APIService {
        if (!APIService.__SELF__) {
            APIService.__SELF__ = new APIService();
        }

        return APIService.__SELF__;
    }

    /**
     * Default http endpoint created automatically by the service, pointing to
     * `${config.baseUrl}`
     */
    get http(): AxiosInstance {
        return this._http!;
    }

    /**
     * Default api endpoint created automatically by the service, pointing to
     * `${config.baseUrl/api}`
     */
    get api(): AxiosInstance {
        return this._api!;
    }

    /**
     * Default api endpoint created automatically by the service, pointing to
     * `${config.baseUrl/api/v2}`
     */
    get internal(): AxiosInstance {
        return this._internal!;
    }

    /**
     * Default endpoints setters
     * @param http
     */
    private setHTTP(http: AxiosInstance | null) {
        this._http = http;
    }

    /**
     * Default endpoints setters
     * @param api
     */
    private setAPI(api: AxiosInstance | null) {
        this._api = api;
    }

    /**
     * Default endpoints setters
     * @param internal
     */
    private setInternal(internal: AxiosInstance | null) {
        this._internal = internal;
    }

    /**
     * Service configuration method, responsible with normalizing config object,
     * and create default endpoints.
     * @param config
     */
    public configure(config: Partial<IAPIOptions>) {
        this.config = { ...DEFAULT_API_OPTIONS, ...this.config, ...config };
        this._configured = true;

        this.config.baseUrl =
            this.config.baseUrl ||
            process.env.REACT_APP_API_ENDPOINT ||
            (this.config.type === 'public'
                ? process.env.NEXT_PUBLIC_API_ENDPOINT
                : process.env.NEXT_PRIVATE_API_ENDPOINT) ||
            '/';

        if (this.config.baseUrl === 'auto') {
            this.config.baseUrl =
                ENDPOINTS[this.config.platform + '_' + process.env.NODE_ENV] ||
                ENDPOINTS[SupportedTags.APPAREL_ORDER + '_' + process.env.NODE_ENV] ||
                '/';
        }

        this.setHTTP(this.createHTTP());
        this.setAPI(this.createHTTP('/api'));
        this.setInternal(this.createHTTP('/api/v2'));
    }

    /**
     * Return service configuration status
     */
    public isConfigured(): boolean {
        return this._configured;
    }

    /**
     * Create Axios instance adding default interceptors to handle request and response.
     * @param path
     * @param axiosConfig
     */
    public createHTTP(path = '', axiosConfig?: AxiosRequestConfig): AxiosInstance {
        if (path.startsWith('http')) {
            throw new Error('Path cannot be a URL');
        }

        const endpoint = Axios.create(axiosConfig);

        endpoint.interceptors.request.use(this.createRequestInterceptor(path), this.createRequestInterceptor(path));
        endpoint.interceptors.response.use(this.createResponseInterceptor(), this.createResponseInterceptor(true));

        return endpoint;
    }

    /**
     * HTTP Request interceptor
     * When a request it's sent we make sure that the request has some helpfully props used by
     * the platform, like default headers ( authorization, x-requested-with, etc.), normalizing fields (aka. return_only)
     * and lot other feature stuff.
     */
    private createRequestInterceptor(path: string): RequestInterceptor {
        return (config: AxiosRequestConfig): AxiosRequestConfig => {
            if (!config.baseURL) {
                config.baseURL = `${this.config.baseUrl ?? ''}`;
            }

            config.baseURL = cleanEndSlash(config.baseURL);

            config.headers = config.headers || {};
            config.headers['X-Requested-With'] = 'XMLHttpRequest';

            if (AuthService.getInstance().hasJWT() && !config?.headers?.Authorization) {
                config.headers.Authorization = `Bearer ${AuthService.getInstance().getJWT()}`;
            }

            config.params = {
                ...config.params,
                timezone_offset: timezoneOffset(),
            };

            const hasParamsFields = !!config?.params?.fields;
            const hasDataFields = !!config?.data?.fields;

            // noinspection JSUnresolvedVariable
            // @ts-ignore
            if (config.fields && !hasParamsFields) {
                // @ts-ignore
                config.params.fields = config.fields;
            }

            if (hasDataFields && !hasParamsFields) {
                config.params.fields = config.data.fields;
                delete config.data.fields;
            }

            if (hasParamsFields) {
                config.params.return_only = config.params.fields;
                delete config.params.fields;
            }

            if (config.params.return_only && typeof config.params.return_only !== 'string') {
                config.params.return_only = ReturnOnly.stringify(config.params.return_only);
            }

            delete config.params.__isLambda;

            if (config.url?.startsWith('http')) {
                config.baseURL = config.url;
                config.url = '';
            } else {
                config.baseURL = APIService.buildURL(config.baseURL, config.params);
                config.url = ensureStartSlash(APIService.buildURL(`${path}/${config.url ?? ''}`, config.params));
            }

            return this.attachProjectNameToConfig(config);
        };
    }

    /**
     * HTTP Response interceptor
     * When a response arrive, if there exist an error that will match one of the
     * defined reasons, we will call automatically logout action to prevent users
     * making not logged requests
     */
    private createResponseInterceptor(errorInterceptor = false) {
        return async (data: AxiosResponse) => {
            const responseData = cleanDataResponse(data);

            if (responseData) {
                data.data = responseData;
            }

            if (data.data.error && REJECTION_REASONS.indexOf(data.data.error) > -1) {
                await AuthService.getInstance().logout();
            }

            if (errorInterceptor) {
                throw data;
            }

            return data;
        };
    }

    /**
     * Create an Axios instance that is related to an endpoint
     * @param endpoint
     * @param axiosConfig
     */
    public createEndpoint(endpoint: string, axiosConfig: AxiosRequestConfig = {}): AxiosInstance {
        if (!endpoint.startsWith('/')) {
            endpoint = `api/${endpoint.replace(/^\//, '')}`;
        }

        return this.createHTTP(endpoint, axiosConfig);
    }

    public createLambdaEndpoint(endpoint: string, axiosConfig: AxiosRequestConfig = {}): AxiosInstance {
        if (!axiosConfig.baseURL) {
            axiosConfig.baseURL = `dev.${APIService.API_GATEWAY}`;

            if (!!process.env.REACT_APP_LAMBDA_ENDPOINT) {
                axiosConfig.baseURL = process.env.REACT_APP_LAMBDA_ENDPOINT;
            } else if (!!process.env.LAMBDA_ENDPOINT) {
                axiosConfig.baseURL = process.env.LAMBDA_ENDPOINT;
            } else if (process.env.NODE_ENV === 'production') {
                axiosConfig.baseURL = `api.${APIService.API_GATEWAY}`;
            }

            axiosConfig.baseURL = `${axiosConfig.baseURL}`.trim();

            if (axiosConfig.baseURL && !/^https?:\/\//.test(axiosConfig.baseURL)) {
                axiosConfig.baseURL = `https://${axiosConfig.baseURL}`;
            }
        }

        axiosConfig.params = axiosConfig.params ?? {};
        axiosConfig.params.__isLambda = true;

        return this.createHTTP(endpoint, axiosConfig);
    }

    public static createEndpoint(endpoint: string, axiosConfig?: AxiosRequestConfig): AxiosInstance {
        return this.getInstance().createEndpoint(endpoint, axiosConfig);
    }

    public static createHTTP(path: string, axiosConfig?: AxiosRequestConfig): AxiosInstance {
        return this.getInstance().createHTTP(path, axiosConfig);
    }

    public static createLambdaEndpoint(endpoint: string, axiosConfig?: AxiosRequestConfig): AxiosInstance {
        return this.getInstance().createLambdaEndpoint(endpoint, axiosConfig);
    }

    /**
     * Bring service to the default state
     */
    public reset() {
        this.config = DEFAULT_API_OPTIONS;
        this._configured = false;

        this.setHTTP(null);
        this.setAPI(null);
        this.setInternal(null);
    }

    /**
     * Static configure alias.
     * @param options
     */
    static configure(options: Partial<IAPIOptions>) {
        APIService.getInstance().configure(options);
    }

    public platform() {
        const tags = new Tags('sdk');

        if (this.config.platform) {
            tags.set('platform', `${this.config.platform}`);
        }

        return tags.toString();
    }

    public attachProjectNameToConfig(config: AxiosRequestConfig) {
        config.params = {
            ...(config.params || {}),
            request_coming_from: config.params?.request_coming_from ?? this.platform(),
            wholesaler_id: config.params?.wholesaler_id ?? this.config.wholesaler_id,
        };

        config.headers = {
            ...(config.headers || {}),
            'X-From': config.headers && config.headers['X-From'] ? config.headers['X-From'] : this.platform(),
        };

        return config;
    }

    public static createWebsocket(endpoint?: string, { platform }: any = {}): WebSocket {
        if (!endpoint) {
            if (!!process.env.REACT_APP_WEBSOCKET_ENDPOINT) {
                endpoint = process.env.REACT_APP_WEBSOCKET_ENDPOINT;
            } else if (!!process.env.WEBSOCKET_ENDPOINT) {
                endpoint = process.env.WEBSOCKET_ENDPOINT;
            } else {
                endpoint = `${APIService.API_GATEWAY_WEBSOCKET}/${process.env.NODE_ENV}`;
            }
        }

        if (!endpoint.startsWith('wss://')) {
            endpoint = `wss://${endpoint}`;
        }

        return new WebSocket(
            `${endpoint}?token=${AuthService.getInstance().getJWT()}&platform=${
                platform ?? SupportedTags.APPAREL_ORDER
            }`,
        );
    }

    private static buildURL(url: string, params: Record<string, any>): string {
        const path = Object.entries(params).reduce(
            (current, [key, value]: [string, any]) => current.replace(`{${key}}`, value).replace(`{ ${key} }`, value),
            url,
        );

        return cleanSlashes(path);
    }

    public static getPlatform(): Platform {
        return new Platform(this.getInstance().config.platform);
    }
}
